/**
 * CategoryHeader.jsx
 *
 * Column header for a requirement category in the curriculum grid.
 * Shows category name, credit requirements, and a mini progress bar.
 * Supports collapsed state to minimize column width.
 */

import PropTypes from "prop-types";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid";
import { useDndContext } from "@dnd-kit/core";
import {
  doesClassificationMatchCategory,
  formatCreditRange,
  getFillPercentage,
} from "../../recoil/curriculumMapSelector";
import { formatEcts } from "../../helpers/formatEcts";

const CategoryHeader = ({
  category,
  isFirst,
  isLast,
  isCollapsed,
  onToggleCollapse,
  isParentComplete = false,
  orientation = "column",
}) => {
  const {
    name,
    minCredits,
    maxCredits,
    earnedCredits = 0,
    plannedCredits = 0,
    isComplete,
    validClassifications,
  } = category;

  // Drag-aware suggestion highlight for the column header
  const { active } = useDndContext();
  const isSuggestedTarget = (() => {
    if (!active) return false;
    const dragData = active.data.current;
    const classification =
      dragData?.type === "eventlist-course"
        ? dragData.course?.classification
        : dragData?.type === "grid-course"
          ? dragData.item?.classification
          : null;
    return doesClassificationMatchCategory(classification, name, validClassifications);
  })();

  // Column is highlighted if this category OR its parent is complete
  const isHighlighted = isComplete || isParentComplete;

  // Displayed total is deliberately uncapped, so a category sitting above its
  // ceiling reads "10 / 9" and the parent's excess badge has a visible source.
  const totalCredits = earnedCredits + plannedCredits;
  const creditRangeLabel = formatCreditRange({ minCredits, maxCredits });

  // The bar, by contrast, tracks the credits that actually count.
  const cappedTotal = Math.min(
    totalCredits,
    maxCredits > 0 ? maxCredits : Infinity,
  );
  const fillPercentage = getFillPercentage(cappedTotal, {
    minCredits,
    maxCredits,
  });

  // Truncate long names
  const displayName = name.length > 22 ? name.substring(0, 20) + "..." : name;

  // Border radius for first/last columns when no parent headers
  const roundedClasses = `${isFirst ? "rounded-tl-lg" : ""} ${
    isLast ? "rounded-tr-lg" : ""
  }`;

  const suggestionClass = isSuggestedTarget
    ? "ring-2 ring-blue-300 ring-inset"
    : "";

  // Row orientation — used as a sticky-left row label in flipped grid mode
  if (orientation === "row") {
    return (
      <div
        className={`relative p-2 sticky left-0 z-10 border-b border-gray-100 flex flex-col justify-center min-h-[70px] overflow-hidden ${suggestionClass}`}
        style={{ backgroundColor: "#f9fafb" }}
        title={name}
      >
        {/* Progress fill background */}
        <div
          className={`absolute inset-y-0 left-0 transition-all duration-300 ${
            isHighlighted ? "bg-green-100" : "bg-gray-200"
          }`}
          style={{ width: isHighlighted ? "100%" : `${fillPercentage}%` }}
        />

        <div className="relative z-10">
          <div className="text-xs font-semibold text-gray-800 leading-tight truncate">
            {displayName}
          </div>
          <div className="text-[10px] text-gray-700 mt-0.5 whitespace-nowrap">
            <span
              className={
                isComplete ? "font-semibold text-green-700" : "font-medium"
              }
            >
              {formatEcts(totalCredits)}
            </span>
            <span className="text-gray-500">
              {" / "}
              {creditRangeLabel || "?"}
            </span>
            <span className="text-gray-500 ml-0.5">ECTS</span>
          </div>
        </div>
      </div>
    );
  }

  // Collapsed view - vertical name with expand button
  if (isCollapsed) {
    const abbreviation = name.length > 10 ? name.substring(0, 8) + "…" : name;

    return (
      <div
        className={`relative border-b border-gray-100 min-h-[70px] py-1 cursor-pointer overflow-hidden transition-colors ${roundedClasses} ${suggestionClass}`}
        style={{ backgroundColor: '#f9fafb' }}
        onClick={onToggleCollapse}
        title={`${name} (click to expand)`}
      >
        {/* Progress fill background - highlighted if this or parent is complete */}
        <div
          className={`absolute inset-y-0 left-0 transition-all duration-300 ${
            isHighlighted ? 'bg-green-100' : 'bg-gray-200'
          }`}
          style={{ width: isHighlighted ? '100%' : `${fillPercentage}%` }}
        />

        {/* Content layer */}
        <div className="relative z-10 flex flex-col items-center justify-between h-full min-h-[62px]">
          {/* Vertical rotated name */}
          <div
            className="text-[9px] font-medium text-gray-600 whitespace-nowrap overflow-hidden"
            style={{
              writingMode: "vertical-rl",
              textOrientation: "mixed",
              transform: "rotate(180deg)",
              maxHeight: "50px",
            }}
          >
            {abbreviation}
          </div>

          {/* Expand icon */}
          <ChevronRightIcon className="w-3 h-3 text-gray-500" />

          {/* Completion indicator via green dot - only for this category's own completion */}
          {isComplete && (
            <div className="w-2 h-2 rounded-full bg-green-600" title="Complete" />
          )}
        </div>
      </div>
    );
  }

  // Expanded view with fill progress indicator
  return (
    <div
      className={`relative p-2 border-b border-gray-100 flex flex-col justify-between min-h-[70px] overflow-hidden ${roundedClasses} ${suggestionClass}`}
      style={{ backgroundColor: '#f9fafb' }}
      title={name}
    >
      {/* Progress fill background - highlighted if this or parent is complete */}
      <div
        className={`absolute inset-y-0 left-0 transition-all duration-300 ${
          isHighlighted ? 'bg-green-100' : 'bg-gray-200'
        }`}
        style={{ width: isHighlighted ? '100%' : `${fillPercentage}%` }}
      />

      {/* Content layer */}
      <div className="relative z-10 flex flex-col justify-between h-full">
        {/* Header row with name and collapse button */}
        <div className="flex items-start justify-between gap-1">
          <div className="text-xs font-semibold text-gray-800 leading-tight flex-1">
            {displayName}
          </div>
          <button
            onClick={onToggleCollapse}
            className="p-0.5 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
            title="Collapse column"
          >
            <ChevronLeftIcon className="w-3 h-3 text-gray-500" />
          </button>
        </div>

        {/* Credit info - simplified X/Y ECTS format */}
        <div className="flex items-center justify-between mt-auto">
          <div className="text-[10px] text-gray-700 whitespace-nowrap">
            <span className={isComplete ? "font-semibold text-green-700" : "font-medium"}>
              {formatEcts(totalCredits)}
            </span>
            <span className="text-gray-500">
              {" / "}
              {creditRangeLabel || "?"}
            </span>
            <span className="text-gray-500 ml-0.5">ECTS</span>
          </div>
          {isComplete && (
            <div className="w-2 h-2 rounded-full bg-green-600" title="Complete" />
          )}
        </div>
      </div>
    </div>
  );
};

CategoryHeader.propTypes = {
  category: PropTypes.shape({
    path: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    minCredits: PropTypes.number,
    maxCredits: PropTypes.number,
    earnedCredits: PropTypes.number,
    plannedCredits: PropTypes.number,
    isComplete: PropTypes.bool,
    validClassifications: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  isParentComplete: PropTypes.bool,
  orientation: PropTypes.oneOf(["column", "row"]),
};

CategoryHeader.defaultProps = {
  isFirst: false,
  isLast: false,
  isCollapsed: false,
  onToggleCollapse: () => {},
  isParentComplete: false,
  orientation: "column",
};

export default CategoryHeader;
